import React, { useState, useMemo } from 'react';
import { __ } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';
import { klona } from 'klona';

// store
import { storeSet, useStoreValue } from '../../store';

import './../../assets/components/ModalAssignMemberToCuesheetItem.css';

// components
import FormField from '../FormField';
import ButtonFlat from '../ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../ButtonWrapper/components/ButtonFormSubmit';
import ModalDialog from '../ModalDialog';
import UserAvatar from '../UserAvatar';

const ModalAssignMemberToCuesheetItem = ({
                                             projectId = '',
                                             cueSheetId = '',
                                             assignedUser = null,
                                             headerText = __('Assegna', 'soundbetween')
                                         }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const {
        control,
        handleSubmit,
        formState: { isValid }
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            teamMembers: [],
            notes: ''
        }
    });
    const team = useStoreValue('team');

    const onSubmit = async (formData) => {
        const updatedFormData = klona({ ...formData });
        setLoading(true);

        try {
            // Assign permissions to all selected team members
            const promises = updatedFormData.teamMembers.map(userId => {
                return storeSet('assignPermissions', projectId, [
                    ['userId', userId],
                    ['cueSheetId', cueSheetId]
                ]);
            });

            // Wait for all assignments to complete
            await Promise.all(promises);

            storeSet('refetchCueSheet', projectId);

            // Close modal on success
            setModalVisible(false);
        } catch (error) {
            // Error is already displayed by the action's displayNetworkError
            // We just catch it here to prevent unhandled promise rejection
            console.error('Failed to assign permissions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setModalVisible(true);
    }

    // Prepare team members options for multiselect (formatted with value/label)
    const teamOptions = useMemo(() => {
        return team.map(member => ({
            ...member,
            value: member.userId,
            label: `${member.name || ''} ${member.surname || ''}`.trim() || member.invitedEmail
        }));
    }, [team]);

    // Custom render for selected items with avatars
    const renderSelectedUser = (user, handleRemove) => {
        return (
            <>
                <UserAvatar
                    url={user.profile_picture_url}
                    name={user.label}
                />
                <span className="multiSelectDropdown__selectedItemLabel">
                    {user.label}
                </span>
                <button
                    type="button"
                    onClick={() => handleRemove(user.value)}
                    className="multiSelectDropdown__removeBtn"
                    aria-label={__('Rimuovi utente', 'soundbetween')}
                />
            </>
        );
    };

    return (
        <>
            <div className="assignCuesheetItem">
                {assignedUser && (
                    <div className="assignCuesheetItem__avatars">
                        <UserAvatar
                            url={assignedUser.profile_picture_url}
                            name={`${assignedUser.name} ${assignedUser.surname}`.trim()}
                        />
                    </div>
                )}
                {assignedUser
                    ? <button
                        type="button"
                        className="assignCuesheetItem__addBtn"
                        onClick={handleCreateNew}
                        aria-label={__('Aggiungi assegnatario', 'soundbetween')}
                      >+</button>
                    : <ButtonFlat size="sm" onClick={handleCreateNew}>{__('Assegna', 'soundbetween')}</ButtonFlat>
                }
            </div>
            {modalVisible
                ? <ModalDialog
                    additionalClasses={['modalCreateProject']}
                    header={headerText}
                    visible={modalVisible}
                    onHide={() => {
                        if (!modalVisible) return;
                        setModalVisible(false);
                    }}>
                    <form className="appForm formInModal" onSubmit={handleSubmit(onSubmit)}>
                        <FormField
                            type="multiselect"
                            name="teamMembers"
                            label={__('Seleziona Membri del Team', 'soundbetween')}
                            control={control}
                            initialOptions={teamOptions}
                            labelKey="label"
                            valueKey="value"
                            placeholder={__('Cerca membri...', 'soundbetween')}
                            displayMode="count"
                            showClearAll={true}
                            renderSelectedItem={renderSelectedUser}
                            rules={{
                                required: __('Seleziona almeno un membro', 'soundbetween')
                            }}
                        />

                        <FormField
                            type="textarea"
                            name="notes"
                            rows={7}
                            label={__('Aggiungi note', 'soundbetween')}
                            control={control}
                            config={{ required: __('È obbligatorio', 'soundbetween') }}
                        />

                        <div className="appFormActions">
                            <ButtonFlat size="lg" type="button" onClick={() => {
                                if (!modalVisible) return;
                                setModalVisible(false);
                            }}>
                                {__('Anulla', 'soundbetween')}
                            </ButtonFlat>
                            <ButtonFormSubmit disabled={loading || !isValid}>
                                {__('Salva', 'soundbetween')}
                            </ButtonFormSubmit>
                        </div>
                    </form>
                </ModalDialog> : null}
        </>
    )
}

export default ModalAssignMemberToCuesheetItem;